import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  colors,
  DialogActions,
  DialogContent,
  Divider,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  bulkUpdateEquipmentDefaultRates,
  compareEquipmentDefaultRatesImport,
  compareEquipmentDefaultRatesUpdateImport,
  downloadEquipmentDefaultRatesNewTemplate,
  downloadEquipmentDefaultRatesUpdateTemplate,
  hasExistingEquipmentDefaultRates,
  listEquipmentItems,
  BulkUpdateEquipmentRatesResponse,
} from 'api/equipmentApi';
import { handleMutationError } from 'util/handleMutationError';
import BulkUpdateDefaultRatesForm from './BulkUpdateDefaultRatesForm';
import BulkUpdateDefaultRatesResultView from './BulkUpdateDefaultRatesResultView';
import { TabProps } from './ScopedRateSharedTab';

const DefaultRatesTab = ({ mode, onBusyChange, onClose }: TabProps) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BulkUpdateEquipmentRatesResponse | null>(
    null
  );
  const [result, setResult] = useState<BulkUpdateEquipmentRatesResponse | null>(
    null
  );

  const { data: equipmentData } = useQuery(
    ['equipment-rate-items'],
    listEquipmentItems
  );
  const noExistingData = (equipmentData?.length ?? 0) === 0;

  const { data: hasExistingRates } = useQuery(
    ['equipment-default-rates-exist'],
    hasExistingEquipmentDefaultRates,
    { enabled: mode === 'update' && !noExistingData }
  );
  const noExistingRates = mode === 'update' && hasExistingRates === false;

  const { mutate: previewMutate, isLoading: isPreviewLoading } = useMutation(
    mode === 'update'
      ? compareEquipmentDefaultRatesUpdateImport
      : compareEquipmentDefaultRatesImport,
    {
      onSuccess: (data) => setPreview(data),
      onError: (error) => {
        enqueueSnackbar(
          `Failed to read the file: ${handleMutationError(error)}`,
          { variant: 'error' }
        );
      },
    }
  );

  const { mutate: commitMutate, isLoading: isCommitLoading } = useMutation(
    bulkUpdateEquipmentDefaultRates,
    {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(['equipment-rate-items']);
        setResult(data);
        enqueueSnackbar(
          data.errorCount === 0
            ? 'Equipment default rates updated successfully'
            : `Equipment default rates updated — ${data.errorCount} row(s) skipped`,
          { variant: 'success' }
        );
      },
      onError: (error) => {
        enqueueSnackbar(
          `Failed to update equipment default rates: ${handleMutationError(error)}`,
          { variant: 'error' }
        );
      },
    }
  );

  const { mutate: downloadMutate, isLoading: isDownloadingTemplate } = useMutation(
    mode === 'update'
      ? downloadEquipmentDefaultRatesUpdateTemplate
      : downloadEquipmentDefaultRatesNewTemplate,
    {
      onError: (error) => {
        enqueueSnackbar(
          `Failed to download the template: ${handleMutationError(error)}`,
          { variant: 'error' }
        );
      },
    }
  );

  const isBusy = isPreviewLoading || isCommitLoading || isDownloadingTemplate;
  React.useEffect(() => onBusyChange(isBusy), [isBusy, onBusyChange]);

  const step = result ? 'result' : preview ? 'preview' : 'form';

  const resultDivergedFromPreview =
    !!preview &&
    !!result &&
    (preview.createdCount !== result.createdCount ||
      preview.updatedCount !== result.updatedCount ||
      preview.ignoredCount !== result.ignoredCount ||
      preview.errorCount !== result.errorCount);

  return (
    <>
      {step === 'form' && (
        <>
          <DialogContent sx={{ p: 0 }}>
            <BulkUpdateDefaultRatesForm
              file={file}
              setFile={setFile}
              isLoading={isPreviewLoading}
              mode={mode}
              noExistingData={noExistingData}
              noExistingRates={noExistingRates}
              onDownloadTemplate={() => downloadMutate()}
              isDownloadingTemplate={isDownloadingTemplate}
            />
          </DialogContent>
          <Divider />
          <DialogActions>
            <Box mr={3} mt={1} mb={2}>
              <Button
                onClick={onClose}
                sx={{ color: colors.grey[600], mr: 1 }}
                disabled={isPreviewLoading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={isPreviewLoading || !file}
                endIcon={
                  isPreviewLoading ? (
                    <CircularProgress size={15} />
                  ) : (
                    <ArrowForwardIcon />
                  )
                }
                onClick={() => {
                  if (!file) return;
                  previewMutate({ file });
                }}>
                {isPreviewLoading ? 'Please wait...' : 'Preview'}
              </Button>
            </Box>
          </DialogActions>
        </>
      )}

      {step === 'preview' && preview && (
        <>
          <BulkUpdateDefaultRatesResultView response={preview} />
          <Divider />
          <DialogActions>
            <Box mr={3} mt={1} mb={2}>
              <Button
                onClick={() => setPreview(null)}
                sx={{ color: colors.grey[600], mr: 1 }}
                disabled={isCommitLoading}>
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={
                  isCommitLoading ||
                  !preview.previewToken ||
                  (preview.createdCount === 0 && preview.updatedCount === 0)
                }
                endIcon={
                  isCommitLoading ? <CircularProgress size={15} /> : <CheckIcon />
                }
                onClick={() => {
                  if (!preview.previewToken) return;
                  commitMutate({ previewToken: preview.previewToken });
                }}>
                {isCommitLoading
                  ? 'Please wait...'
                  : preview.errorCount > 0
                    ? 'Ignore Errors and Continue'
                    : 'Confirm Import'}
              </Button>
            </Box>
          </DialogActions>
        </>
      )}

      {step === 'result' && result && (
        <>
          {resultDivergedFromPreview && (
            <Alert severity="warning" sx={{ m: 2 }}>
              The committed result differs from the preview you approved,
              data may have changed, or effective dates rolled over since
              preview. Review the details below.
            </Alert>
          )}
          <BulkUpdateDefaultRatesResultView response={result} />
          <DialogActions>
            <Box mr={3} mt={1} mb={2}>
              <Button
                variant="contained"
                color="primary"
                endIcon={<CheckIcon />}
                onClick={onClose}>
                Done
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
    </>
  );
};

export default DefaultRatesTab;
